import { useList } from 'admin';
import { DEFAULT_CONTAINER_TYPE, type Material, SourceChoiceEnum } from 'api';
import { Form, type InferType, type SubmitHandler, useForm, yupResolver } from 'form';
import { useEffect } from 'react';
import { Alert, Box, Button, Page } from 'ui';

import { CostsSection } from '../../components/CostsSection/CostsSection';
import { CurrencyRates } from '../../components/CurrencyRates/CurrencyRates';
import { LogisticsSection } from '../../components/LogisticsSection/LogisticsSection';
import { MaterialSection } from '../../components/MaterialSection/MaterialSection';
import { PaymentSection } from '../../components/PaymentSection/PaymentSection';
import { ResultTable } from '../../components/ResultTable/ResultTable';
import { RevenuesSection } from '../../components/RevenuesSection/RevenuesSection';
import { useDealProfitabilityCalculator } from '../../hooks';
import { dealProfitabilityCalculatorSchema } from './schema';

export type FormValues = InferType<typeof dealProfitabilityCalculatorSchema>;

const PERN_CLAIMABLE_ORIGIN_COUNTRIES = ['GB'];
const DEFAULT_CURRENCY = 'GBP';
const DEFAULT_VALUES: Partial<FormValues> = {
  buyer_agent_commission: 0,
  buyer_agent_commission_currency: DEFAULT_CURRENCY,
  buyer_currency: DEFAULT_CURRENCY,
  buyer_deposit_percentage: 0,
  buyer_due_date_offset: 0,
  buyer_due_date_offset_direction: 1,
  container_type: DEFAULT_CONTAINER_TYPE,
  pern_currency: DEFAULT_CURRENCY,
  pern_revenue_per_tonne: 0,
  seller_agent_commission: 0,
  seller_agent_commission_currency: DEFAULT_CURRENCY,
  seller_currency: DEFAULT_CURRENCY,
  seller_deposit_percentage: 0,
  seller_due_date_offset: 0,
  seller_due_date_offset_direction: 1,
  shipping_cost_per_load: 0,
  shipping_cost_per_load_currency: DEFAULT_CURRENCY,
  transport_cost_currency: DEFAULT_CURRENCY,
  transport_cost_source: SourceChoiceEnum.ESTIMATE,
};

export const DealProfitabilityCalculator = () => {
  const form = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver<FormValues>(dealProfitabilityCalculatorSchema),
  });
  const {
    formState: { isDirty, errors },
    reset,
    setValue,
    watch,
  } = form;

  const watchMaterial = watch('material');
  const watchOriginAddress = watch('origin_address');
  const watchDestinationAddress = watch('destination_address');
  const watchOriginPortCountry = watch('origin_port_country');
  const watchDestinationPortCountry = watch('destination_port_country');

  // Fetching all materials to get the market pern price per tonne
  const { data: materials } = useList<Material>({
    dataProviderName: 'materials',
    resource: '/v1/materials/',
  });

  useEffect(() => {
    const originCountryCode = watchOriginAddress?.country_code || watchOriginPortCountry;
    const destinationCountryCode = watchDestinationAddress?.country_code || watchDestinationPortCountry;
    if (
      (originCountryCode && !PERN_CLAIMABLE_ORIGIN_COUNTRIES.includes(originCountryCode)) ||
      (destinationCountryCode && destinationCountryCode === originCountryCode)
    ) {
      setValue('pern_revenue_per_tonne', 0);
      setValue('pern_currency', DEFAULT_CURRENCY);
      return;
    }

    if (watchMaterial) {
      // Assign market pern price per tonne to pern_revenue_per_tonne when material changes
      const marketPernPricePerTonne = Number(
        materials?.data.find((element) => element.id === watchMaterial)?.market_pern_price_per_tonne ?? 0
      );
      if (marketPernPricePerTonne) {
        setValue('pern_revenue_per_tonne', marketPernPricePerTonne);
        setValue('pern_currency', DEFAULT_CURRENCY);
      }
    }
  }, [
    materials,
    setValue,
    watchMaterial,
    watchOriginAddress,
    watchDestinationAddress,
    watchOriginPortCountry,
    watchDestinationPortCountry,
  ]);

  const { data, handleCalculateProfitability, isProcessing } = useDealProfitabilityCalculator(form);
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    await handleCalculateProfitability(data);
    // formState.isDirty compares the current form state against the default values.
    // We want to keep track of the changes made to the form after the last calculation.
    reset(data, { keepDefaultValues: false, keepValues: true });
  };
  return (
    <Page>
      <Page.Title title="Deal Profitability Calculator" />
      <Form {...form} onSubmit={onSubmit}>
        {errors.root ? <Alert severity="error">{String(errors.root.message)}</Alert> : null}
        <MaterialSection />
        <LogisticsSection />
        <PaymentSection />
        <RevenuesSection />
        <CostsSection />
        <Box display="flex" justifyContent="center" alignItems="center" paddingBlockEnd={3}>
          <Button type="submit" size="large" loading={isProcessing} disabled={!isDirty}>
            {isDirty ? 'Recalculate' : 'Results are synced'}
          </Button>
        </Box>
        {data ? <ResultTable data={data} /> : null}
        {data?.fx_rates ? <CurrencyRates fxRates={data.fx_rates} /> : null}
      </Form>
    </Page>
  );
};
